import React from 'react';
import PropTypes from 'prop-types';
import { Box } from 'rebass/styled-components';
import freedomTheme from 'web_component_library/theme/freedom';
import stringInterpolation from 'web_component_library/utils/stringInterpolation';
import Button from 'web_component_library/button';
import A11yContent from 'web_component_library/A11yContent';
import { PauseIcon, PlayIcon, LeftArrowIcon, RightArrowIcon } from './icons';

const { colors, borderRadius } = freedomTheme;

export const NavBarLegacy = ({
  children,
  scrollTo,
  handlePlayPause,
  isRunning,
  selectSlideText,
  playText,
  pauseText,
  selectedIndex,
  viewDuration = null,
  time = null,
  showProgressBar,
  onPrevClick,
  isPrevDisabled,
  previousSlideText,
  onNextClick,
  isNextDisabled,
  nextSlideText,
}) => {
  if (React.Children.toArray(children).length <= 1) {
    return null;
  }
  return (
    <Box
      data-testid="navbarCarrousel"
      sx={{
        width: '100%',
        zIndex: 9,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        gap: 'lg',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 'md',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 'md',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            padding: '1em 1.5em',
            borderRadius: `${borderRadius.button}px`,
          }}
        >
          <Button
            variant="none"
            css={{
              width: '1rem',
              height: '1rem',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: colors.white,
              color: colors.accessibleOrange,
              padding: 0,
            }}
            onClick={handlePlayPause}
          >
            {isRunning ? (
              <PauseIcon style={{ width: 'inherit', height: 'inherit' }} />
            ) : (
              <PlayIcon style={{ width: 'inherit', height: 'inherit' }} />
            )}
            <A11yContent>{isRunning ? pauseText : playText}</A11yContent>
          </Button>

          {React.Children.toArray(children).map((child, index) => (
            <Button
              // eslint-disable-next-line react/no-array-index-key
              key={`${child?.props?.entryTitle}-${index}`}
              variant="none"
              onClick={() => scrollTo(index)}
              borderRadius="0.5rem"
              sx={{
                width: index === selectedIndex ? '3rem' : '1rem',
                height: '1rem',
                display: 'flex',
                alignItems: 'center',
                backgroundColor:
                  index === selectedIndex && !showProgressBar
                    ? colors.accessibleOrange
                    : colors.grey,
                transition: 'all 300ms',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  ...(index === selectedIndex && showProgressBar
                    ? {
                        content: "' '",
                        width: `${(time / (viewDuration * 1000)) * 100}%`,
                        height: '100%',
                        background: colors.accessibleOrange,
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        transition: 'all 0ms',
                      }
                    : {}),
                },
              }}
              selectSlideText={stringInterpolation(selectSlideText, index + 1)}
            >
              <A11yContent>{selectSlideText}</A11yContent>
            </Button>
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 'sm',
          }}
        >
          <Button
            onClick={onPrevClick}
            disabled={isPrevDisabled}
            variant="none"
            borderRadius="50%"
            css={{
              width: '3rem',
              height: '3rem',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: colors.white,
              color: isPrevDisabled ? colors.grey : colors.accessibleOrange,
              padding: 0,
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            <LeftArrowIcon style={{ width: 'inherit', height: 'inherit' }} />
            <A11yContent>{previousSlideText}</A11yContent>
          </Button>

          <Button
            onClick={onNextClick}
            disabled={isNextDisabled}
            variant="none"
            borderRadius="50%"
            css={{
              width: '3rem',
              height: '3rem',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: colors.white,
              color: isPrevDisabled ? colors.grey : colors.accessibleOrange,
              padding: 0,
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            <RightArrowIcon style={{ width: 'inherit', height: 'inherit' }} />
            <A11yContent>{nextSlideText}</A11yContent>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

NavBarLegacy.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  scrollTo: PropTypes.func.isRequired,
  handlePlayPause: PropTypes.func.isRequired,
  isRunning: PropTypes.bool.isRequired,
  selectSlideText: PropTypes.string.isRequired,
  playText: PropTypes.string.isRequired,
  pauseText: PropTypes.string.isRequired,
  selectedIndex: PropTypes.number.isRequired,
  viewDuration: PropTypes.number,
  time: PropTypes.number,
  showProgressBar: PropTypes.bool.isRequired,
  onPrevClick: PropTypes.func.isRequired,
  isPrevDisabled: PropTypes.bool.isRequired,
  previousSlideText: PropTypes.string.isRequired,
  onNextClick: PropTypes.func.isRequired,
  isNextDisabled: PropTypes.bool.isRequired,
  nextSlideText: PropTypes.string.isRequired,
};
